import { getApiEndpoint } from "../config/config";

// Bulk deal import: upload a pile of offer letters / comp agreements at once,
// let the backend AI read each one (recipient name + draft pay) and match it to
// a rep, then review each and turn the draft into a real deal — instead of
// keying deals in one at a time. Talks to /v1/bulk-deal-import/*; mirrors the
// iOS BulkDealImportService.

const request = async (path, jwt, { method = "GET", body, formData } = {}) => {
  const response = await fetch(getApiEndpoint(path), {
    method,
    headers: {
      ...(formData ? {} : { "Content-Type": "application/json" }),
      Authorization: `Bearer ${jwt}`,
    },
    ...(formData ? { body: formData } : {}),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message || `HTTP ${response.status}`);
  }
  return payload.data;
};

// A document not yet turned into a deal or discarded — what the review list
// still owes the reviewer.
export const isUnresolved = item =>
  item && item.status !== "confirmed" && item.status !== "skipped";

export const isProcessing = item =>
  item && (item.status === "pending" || item.status === "extracting");

export const bulkDealImportService = {
  // Upload a batch. Returns immediately with one row per document; extraction
  // and name-matching run in the background — poll getBatch for progress.
  createBatch(files, jwt, sellingYearId = null) {
    const formData = new FormData();
    files.forEach(file => formData.append("files", file));
    if (sellingYearId != null) {
      formData.append("selling_year_id", String(sellingYearId));
    }
    return request("/v1/bulk-deal-import/batches", jwt, {
      method: "POST",
      formData,
    });
  },

  // Recent batches (newest first) with per-status counts, so a reviewer can
  // resume one they left half-done.
  listBatches: jwt => request("/v1/bulk-deal-import/batches", jwt),

  // One batch's rows: extraction status, draft pay, suggested rep, plus the
  // scoped rep list for the assignment dropdown and a document URL.
  getBatch: (batchId, jwt) =>
    request(`/v1/bulk-deal-import/batches/${batchId}`, jwt),

  // Turn one reviewed item into a real deal for the assigned rep. `payload` is
  // the review-shaped pay body (payDraftPayload) plus rep_id and an optional
  // guarantor_id.
  confirmItem: (itemId, payload, jwt) =>
    request(`/v1/bulk-deal-import/items/${itemId}/confirm`, jwt, {
      method: "POST",
      body: payload,
    }),

  // Discard a document without creating a deal.
  skipItem: (itemId, jwt) =>
    request(`/v1/bulk-deal-import/items/${itemId}/skip`, jwt, {
      method: "POST",
    }),
};
